<mat-toolbar class="toolbar" color="primary">
    <div class="logo-container">
        <!--
            ai-dev-theme: token-driven text wordmark in BOTH schemes (replaces
            the raster logo). Shared app-wordmark component — same markup used
            on the login / forgot-password screens.
        -->
        <app-wordmark />
    </div>

    <span class="spacer"></span>

    <div class="button-group">
        <button
            matButton="filled"
            class="manual-approval-button"
            (click)="openManualApproval()"
            [matBadge]="manualApprovalCount()"
            matBadgeColor="accent"
            [matBadgeHidden]="manualApprovalCount() < 1"
        >
            <span>Manual approval</span>
        </button>
        <button
            mat-icon-button
            class="theme-toggle-button"
            (click)="toggleTheme()"
            [attr.aria-pressed]="isDarkTheme()"
            [attr.aria-label]="
                isDarkTheme() ? 'Switch to light theme' : 'Switch to dark theme'
            "
            [matTooltip]="
                isDarkTheme() ? 'Switch to light theme' : 'Switch to dark theme'
            "
        >
            <mat-icon class="nav-icon">{{
                isDarkTheme() ? "light_mode" : "dark_mode"
            }}</mat-icon>
        </button>

        <button
            mat-icon-button
            class="audio-toggle-button"
            (click)="toggleAudioEnabled()"
            [attr.aria-pressed]="audioEnabled()"
            [attr.aria-label]="
                audioEnabled()
                    ? 'Approval alert sounds on'
                    : 'Approval alert sounds off'
            "
            [matTooltip]="
                audioEnabled()
                    ? 'Approval alert sounds on'
                    : 'Approval alert sounds off'
            "
        >
            <mat-icon
                [class]="audioEnabled() ? 'audio-on-icon' : 'audio-off-icon'"
                >{{
                    audioEnabled() ? "notifications_active" : "notifications_off"
                }}</mat-icon
            >
        </button>

        <button
            mat-icon-button
            class="profile-menu-trigger"
            [matMenuTriggerFor]="profileMenu"
            [attr.aria-label]="
                identity().name
                    ? copy.triggerAriaPrefix + ' ' + identity().name
                    : copy.triggerAriaFallback
            "
        >
            <mat-icon class="nav-icon">account_circle</mat-icon>
        </button>

        <mat-menu #profileMenu="matMenu" class="profile-menu">
            <div class="profile-identity" (click)="$event.stopPropagation()">
                <div class="profile-identity-name">
                    {{ identity().name ?? identityPlaceholder }}
                </div>
                <div class="profile-identity-meta">
                    <span class="profile-identity-label">{{ copy.roleLabel }}</span>
                    <span class="profile-identity-value">{{
                        identity().role ?? identityPlaceholder
                    }}</span>
                </div>
                <div class="profile-identity-meta">
                    <span class="profile-identity-label">{{
                        copy.tenantLabel
                    }}</span>
                    <span class="profile-identity-value">{{
                        identity().tenant ?? identityPlaceholder
                    }}</span>
                </div>
            </div>

            <mat-divider></mat-divider>

            <div class="profile-preferences" (click)="$event.stopPropagation()">
                <div class="profile-section-label">
                    {{ copy.preferencesSectionLabel }}
                </div>

                <div class="profile-pref-row">
                    <mat-slide-toggle
                        [checked]="audioEnabled()"
                        (change)="toggleAudioEnabled()"
                        [attr.aria-label]="copy.audioLabel"
                    >
                        {{ copy.audioLabel }}
                    </mat-slide-toggle>
                </div>

                <div class="profile-pref-row">
                    <mat-form-field appearance="outline" subscriptSizing="dynamic">
                        <mat-label>{{ copy.landingLabel }}</mat-label>
                        <mat-select
                            [value]="landingRoute()"
                            (selectionChange)="onLandingRouteChange($event.value)"
                            [aria-label]="copy.landingLabel"
                        >
                            @for (option of landingOptions; track option.route) {
                                <mat-option [value]="option.route">{{
                                    option.label
                                }}</mat-option>
                            }
                        </mat-select>
                    </mat-form-field>
                </div>

                <div class="profile-pref-row">
                    <mat-form-field appearance="outline" subscriptSizing="dynamic">
                        <mat-label>{{ copy.pageSizeLabel }}</mat-label>
                        <mat-select
                            [value]="pageSize()"
                            (selectionChange)="onPageSizeChange($event.value)"
                            [aria-label]="copy.pageSizeLabel"
                        >
                            @for (size of pageSizeOptions; track size) {
                                <mat-option [value]="size">{{ size }}</mat-option>
                            }
                        </mat-select>
                    </mat-form-field>
                </div>
            </div>

            <mat-divider></mat-divider>

            <button mat-menu-item (click)="logout()">
                <mat-icon>logout</mat-icon>
                <span>{{ copy.logout }}</span>
            </button>

            <mat-divider></mat-divider>

            <!--
                Release/build metadata (AB#9776, UI-273). Deliberately a plain
                non-interactive div, not a mat-menu-item/button — it must not
                be focusable or announced by assistive tech as a menu action.
                click stopPropagation matches the identity/preferences blocks
                above so it never dismisses the menu.
            -->
            <div
                class="profile-release-info"
                (click)="$event.stopPropagation()"
            >
                <span
                    class="profile-release-info-row"
                    [title]="releaseInfoCopy.timestampTitle"
                >
                    {{ releaseTimestamp }}
                    <span class="profile-release-info-tz">{{
                        releaseInfoCopy.timezoneLabel
                    }}</span>
                </span>
                <span class="profile-release-info-row">{{ lastMergedPr }}</span>
            </div>
        </mat-menu>
    </div>
</mat-toolbar>

@if (showSoundOnboarding()) {
    <div class="sound-onboarding-popover" role="status">
        <div class="sound-onboarding-text">
            Approval alert sounds are on. Use the bell toggle in the top bar to
            turn them off.
        </div>
        <button
            matButton
            class="sound-onboarding-dismiss"
            (click)="dismissSoundOnboarding()"
        >
            Got it
        </button>
    </div>
}
